import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import AdminNav from '../components/AdminNav'
import { useAdmin } from '../context/AdminContext'
import {
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'

const BLUE   = '#1877F2'
const TEXT   = '#050505'
const MUTED  = '#65676B'
const BORDER = '#E4E6EB'
const BG     = '#F0F2F5'
const WHITE  = '#FFFFFF'
const GREEN  = '#16A34A'
const AMBER  = '#D97706'
const RED    = '#DC2626'
const PURPLE = '#7C3AED'

const inp = (focused) => ({
  width: '100%', border: '1px solid ' + (focused ? BLUE : BORDER),
  borderRadius: '9px', padding: '8px 12px', fontSize: '12.5px',
  outline: 'none', background: WHITE, color: TEXT,
  fontFamily: 'Space Grotesk, sans-serif', boxSizing: 'border-box',
  transition: 'border-color 0.15s',
})

function Icon({ path, size = 15, color = MUTED, sw = 1.8 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  )
}

const IC = {
  income:  'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  cost:    'M13 17h8m-4-4v8M3 3h6v6H3zm0 12h6v6H3zM15 3h6v6h-6z',
  profit:  'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
  trash:   'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
  plus:    'M12 4v16m8-8H4',
  tax:     'M9 14l6-6m-4-3a1 1 0 11-2 0 1 1 0 012 0zm6 6a1 1 0 11-2 0 1 1 0 012 0z',
  chart:   'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
}

const INCOME_STREAMS = [
  { key: 'subscription',          label: 'Subscription — Starter' },
  { key: 'subscription_growth',   label: 'Subscription — Growth' },
  { key: 'subscription_pro',      label: 'Subscription — Pro' },
  { key: 'subscription_business', label: 'Subscription — Business' },
  { key: 'ad_slot',               label: 'Ad slot sale' },
  { key: 'verified_badge',        label: 'Verified badge' },
  { key: 'website_setup',         label: 'Website setup service' },
  { key: 'account_setup',         label: 'Account setup service' },
  { key: 'consulting',            label: 'Consulting / coaching' },
  { key: 'api_access',            label: 'API access fee' },
  { key: 'white_label',           label: 'White label fee' },
  { key: 'other',                 label: 'Other income' },
]

const COST_CATEGORIES = [
  { key: 'hosting',          label: 'Hosting — Vercel / server',    type: 'fixed' },
  { key: 'database',         label: 'Database — Supabase',          type: 'fixed' },
  { key: 'domain',           label: 'Domain / DNS',                 type: 'fixed' },
  { key: 'software',         label: 'Software / subscriptions',     type: 'fixed' },
  { key: 'salary_super_admin', label: 'Salary — Founder',          type: 'fixed' },
  { key: 'salary_admin',     label: 'Salary — Admin',               type: 'fixed' },
  { key: 'salary_support',   label: 'Salary — Support',             type: 'fixed' },
  { key: 'salary_marketing', label: 'Salary — Marketing',           type: 'fixed' },
  { key: 'salary_developer', label: 'Salary — Developer',           type: 'fixed' },
  { key: 'salary_finance',   label: 'Salary — Finance',             type: 'fixed' },
  { key: 'office_rent',      label: 'Office rent',                  type: 'fixed' },
  { key: 'insurance',        label: 'Insurance',                    type: 'fixed' },
  { key: 'legal',            label: 'Legal / accounting fees',      type: 'fixed' },
  { key: 'loan',             label: 'Loan repayments',              type: 'fixed' },
  { key: 'marketing_ads',    label: 'Marketing — Ads',              type: 'variable' },
  { key: 'marketing_content', label: 'Marketing — Content',        type: 'variable' },
  { key: 'freelancer',       label: 'Freelancer costs',             type: 'variable' },
  { key: 'payment_gateway',  label: 'Payment gateway fees',         type: 'variable' },
  { key: 'bank_charges',     label: 'Bank / transaction charges',   type: 'variable' },
  { key: 'travel',           label: 'Travel and meetings',          type: 'variable' },
  { key: 'equipment',        label: 'Equipment purchase',           type: 'one_time' },
  { key: 'legal_setup',      label: 'Legal registration / setup',   type: 'one_time' },
  { key: 'office_setup',     label: 'Office setup costs',           type: 'one_time' },
  { key: 'other',            label: 'Other costs',                  type: 'variable' },
]

const PAYMENT_METHODS = ['bKash', 'Nagad', 'Rocket', 'Bank transfer', 'PayPal', 'Wise', 'Cash', 'Other']
const CURRENCIES      = ['BDT', 'USD']
const PAGE_SIZE       = 10

function KpiCard({ label, value, sub, accent, iconPath, onClick }) {
  return (
    <div onClick={onClick}
      style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, borderLeft: '3px solid ' + accent, padding: '16px 18px', cursor: onClick ? 'pointer' : 'default' }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)' }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
        <span style={{ fontSize: '11.5px', fontWeight: 500, color: MUTED }}>{label}</span>
        <div style={{ width: '28px', height: '28px', borderRadius: '7px', background: accent + '15', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon path={iconPath} color={accent} size={13} sw={2} />
        </div>
      </div>
      <div style={{ fontSize: '22px', fontWeight: 800, color: TEXT, letterSpacing: '-0.5px', marginBottom: '3px' }}>{value}</div>
      {sub && <div style={{ fontSize: '11px', color: MUTED }}>{sub}</div>}
    </div>
  )
}

function SField({ label, value, onChange, type = 'text', options, placeholder, optional }) {
  const [focused, setFocused] = useState(false)
  if (options) {
    return (
      <div>
        <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '4px' }}>
          {label}{optional && <span style={{ color: '#B0B3B8', fontWeight: 400, marginLeft: '4px' }}>optional</span>}
        </label>
        <select value={value} onChange={e => onChange(e.target.value)} style={inp(focused)}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}>
          {options.map(o => typeof o === 'string'
            ? <option key={o} value={o}>{o}</option>
            : <option key={o.key} value={o.key}>{o.label}</option>
          )}
        </select>
      </div>
    )
  }
  return (
    <div>
      <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '4px' }}>
        {label}{optional && <span style={{ color: '#B0B3B8', fontWeight: 400, marginLeft: '4px' }}>optional</span>}
      </label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} style={inp(focused)}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
    </div>
  )
}

function AdminFinance() {
  const { admin } = useAdmin()
  const canEdit = admin?.role === 'super_admin' || admin?.role === 'finance'
  const canView = admin?.role === 'super_admin' || admin?.role === 'admin' || admin?.role === 'finance'

  const [period, setPeriod]           = useState('this_month')
  const [activeTab, setActiveTab]     = useState('overview')
  const [loading, setLoading]         = useState(true)
  const [income, setIncome]           = useState([])
  const [costs, setCosts]             = useState([])
  const [sellers, setSellers]         = useState([])
  const [settings, setSettings]       = useState({})
  const [showAddIncome, setShowAddIncome] = useState(false)
  const [showAddCost, setShowAddCost]     = useState(false)
  const [savingIncome, setSavingIncome]   = useState(false)
  const [savingCost, setSavingCost]       = useState(false)
  const [saved, setSaved]             = useState('')
  const [taxRate, setTaxRate]         = useState(15)
  const [incomePage, setIncomePage]   = useState(1)
  const [costPage, setCostPage]       = useState(1)
  const [expandedIncome, setExpandedIncome] = useState(null)
  const [expandedCost, setExpandedCost]     = useState(null)

  const [newIncome, setNewIncome] = useState({
    stream: 'subscription', seller_id: '', amount: '', currency: 'BDT',
    plan: '', period_months: 1, payment_method: 'bKash',
    notes: '', received_at: new Date().toISOString().split('T')[0],
  })

  const [newCost, setNewCost] = useState({
    name: '', category: 'hosting', cost_type: 'fixed',
    amount: '', currency: 'BDT', frequency: 'monthly',
    employee_role: '', notes: '',
  })

  const [stats, setStats] = useState({
    totalIncome: 0, totalIncomeUSD: 0,
    subscriptionIncome: 0, adIncome: 0, serviceIncome: 0, otherIncome: 0,
    totalFixedCosts: 0, totalVariableCosts: 0, totalOneTimeCosts: 0,
    netProfit: 0, incomeByStream: {}, monthlyTrend: [],
  })

  useEffect(() => {
    if (canView) fetchAll()
  }, [period]) // eslint-disable-line react-hooks/exhaustive-deps

  const getDateRange = () => {
    const now = new Date()
    if (period === 'this_month')    return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: now }
    if (period === 'last_month')    return { start: new Date(now.getFullYear(), now.getMonth() - 1, 1), end: new Date(now.getFullYear(), now.getMonth(), 0) }
    if (period === 'last_3_months') return { start: new Date(now.getFullYear(), now.getMonth() - 3, 1), end: now }
    return { start: new Date(now.getFullYear(), 0, 1), end: now }
  }

  const fetchAll = async () => {
    setLoading(true)
    const { start, end } = getDateRange()

    const { data: settingsData } = await supabase.from('admin_settings').select('key, value')
    const sMap = {}
    settingsData?.forEach(s => { sMap[s.key] = s.value })
    setSettings(sMap)
    const tr = Number(sMap.admin_tax_rate_bdt) || 15
    setTaxRate(tr)
    const usdRate = Number(sMap.admin_usd_rate) || 110

    const [{ data: incomeData }, { data: allCosts }, { data: sellersData }] = await Promise.all([
      supabase.from('admin_income').select('*, sellers(shop_name)').gte('received_at', start.toISOString()).lte('received_at', end.toISOString()).order('received_at', { ascending: false }),
      supabase.from('admin_costs').select('*').order('created_at', { ascending: false }),
      supabase.from('sellers').select('id, shop_name').order('shop_name'),
    ])

    setIncome(incomeData || [])
    setCosts(allCosts || [])
    setSellers(sellersData || [])

    const allIncome = incomeData || []
    const costsData = allCosts  || []

    const toBDT = (item) => Number(item.amount || 0) * (item.currency === 'USD' ? usdRate : 1)

    const totalIncomeBDT     = allIncome.reduce((s, i) => s + toBDT(i), 0)
    const subscriptionIncome = allIncome.filter(i => i.stream?.startsWith('subscription')).reduce((s, i) => s + toBDT(i), 0)
    const adIncome           = allIncome.filter(i => i.stream === 'ad_slot').reduce((s, i) => s + toBDT(i), 0)
    const serviceIncome      = allIncome.filter(i => ['website_setup','account_setup','consulting','verified_badge'].includes(i.stream)).reduce((s, i) => s + toBDT(i), 0)
    const otherIncome        = Math.max(0, totalIncomeBDT - subscriptionIncome - adIncome - serviceIncome)

    const incomeByStream = {}
    allIncome.forEach(i => {
      const key = INCOME_STREAMS.find(s => s.key === i.stream)?.label || i.stream
      incomeByStream[key] = (incomeByStream[key] || 0) + toBDT(i)
    })

    const months = period === 'this_month' || period === 'last_month' ? 1 : period === 'last_3_months' ? 3 : 12
    let totalFixedCosts = 0, totalVariableCosts = 0, totalOneTimeCosts = 0
    costsData.forEach(c => {
      const amt = Number(c.amount || 0) * (c.currency === 'USD' ? usdRate : 1)
      if (c.cost_type === 'fixed')      totalFixedCosts    += c.frequency === 'yearly' ? (amt / 12) * months : amt * months
      else if (c.cost_type === 'variable') totalVariableCosts += amt * months
      else                              totalOneTimeCosts  += amt
    })

    const netProfit = totalIncomeBDT - totalFixedCosts - totalVariableCosts - totalOneTimeCosts

    // Monthly trend — last 6 months
    const now2 = new Date()
    const monthlyTrend = []
    for (let i = 5; i >= 0; i--) {
      const d    = new Date(now2.getFullYear(), now2.getMonth() - i, 1)
      const next = new Date(now2.getFullYear(), now2.getMonth() - i + 1, 1)
      const monthIncome = allIncome
        .filter(inc => { const rd = new Date(inc.received_at); return rd >= d && rd < next })
        .reduce((s, inc) => s + toBDT(inc), 0)
      monthlyTrend.push({
        month: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()],
        income: Math.round(monthIncome),
      })
    }

    setStats({ totalIncome: totalIncomeBDT, totalIncomeUSD: totalIncomeBDT / usdRate, subscriptionIncome, adIncome, serviceIncome, otherIncome, totalFixedCosts, totalVariableCosts, totalOneTimeCosts, netProfit, incomeByStream, monthlyTrend })
    setLoading(false)
  }

  const handleAddIncome = async () => {
    if (!newIncome.amount || !newIncome.stream) return
    setSavingIncome(true)
    await supabase.from('admin_income').insert({
      stream: newIncome.stream, seller_id: newIncome.seller_id || null,
      amount: Number(newIncome.amount), currency: newIncome.currency,
      plan: newIncome.plan || null, period_months: Number(newIncome.period_months) || 1,
      payment_method: newIncome.payment_method, notes: newIncome.notes || null,
      received_at: new Date(newIncome.received_at).toISOString(),
    })
    await fetchAll()
    setNewIncome({ stream: 'subscription', seller_id: '', amount: '', currency: 'BDT', plan: '', period_months: 1, payment_method: 'bKash', notes: '', received_at: new Date().toISOString().split('T')[0] })
    setShowAddIncome(false)
    setSavingIncome(false)
    setSaved('Income recorded!')
    setTimeout(() => setSaved(''), 3000)
  }

  const handleAddCost = async () => {
    if (!newCost.name || !newCost.amount) return
    setSavingCost(true)
    await supabase.from('admin_costs').insert({
      name: newCost.name, category: newCost.category, cost_type: newCost.cost_type,
      amount: Number(newCost.amount), currency: newCost.currency, frequency: newCost.frequency,
      employee_role: newCost.employee_role || null, notes: newCost.notes || null,
    })
    await fetchAll()
    setNewCost({ name: '', category: 'hosting', cost_type: 'fixed', amount: '', currency: 'BDT', frequency: 'monthly', employee_role: '', notes: '' })
    setShowAddCost(false)
    setSavingCost(false)
    setSaved('Cost added!')
    setTimeout(() => setSaved(''), 3000)
  }

  const fmt = (n) => {
    const v = Math.abs(Math.round(n || 0))
    if (v >= 1000000) return '৳' + (v / 1000000).toFixed(1) + 'M'
    if (v >= 100000)  return '৳' + Math.round(v / 1000) + 'k'
    return '৳' + v.toLocaleString()
  }

  const totalCosts      = stats.totalFixedCosts + stats.totalVariableCosts + stats.totalOneTimeCosts
  const taxAmount       = stats.netProfit > 0 ? (stats.netProfit * taxRate) / 100 : 0
  const profitAfterTax  = stats.netProfit - taxAmount
  const margin          = stats.totalIncome > 0 ? Math.round((stats.netProfit / stats.totalIncome) * 100) : 0
  const usdRate         = Number(settings.admin_usd_rate) || 110
  const multiplier      = period === 'this_month' || period === 'last_month' ? 12 : period === 'last_3_months' ? 4 : 1

  const incomePaged  = income.slice((incomePage - 1) * PAGE_SIZE, incomePage * PAGE_SIZE)
  const incomeTotalP = Math.max(1, Math.ceil(income.length / PAGE_SIZE))
  const costPaged    = costs.slice((costPage - 1) * PAGE_SIZE, costPage * PAGE_SIZE)
  const costTotalP   = Math.max(1, Math.ceil(costs.length / PAGE_SIZE))

  const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'income',   label: 'Income' },
    { key: 'costs',    label: 'Costs' },
    { key: 'pnl',      label: 'P&L Statement' },
  ]

  const PERIODS = [
    { key: 'this_month',    label: 'This month' },
    { key: 'last_month',    label: 'Last month' },
    { key: 'last_3_months', label: '3 months' },
    { key: 'this_year',     label: 'This year' },
  ]

  if (!canView) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: BG }}>
        <AdminNav />
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px' }}>
          <div style={{ fontSize: '32px' }}>🔒</div>
          <div style={{ fontSize: '13px', color: MUTED }}>Finance data is restricted to admin and finance roles only</div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: BG, fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
      <AdminNav />
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <div style={{ padding: '24px 28px' }}>
          <style>{`
            @media(max-width:960px){ .fin-kpi{grid-template-columns:1fr 1fr!important} }
            @media(max-width:600px){ .fin-kpi{grid-template-columns:1fr!important} .fin-charts{grid-template-columns:1fr!important} }
          `}</style>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 800, color: TEXT, letterSpacing: '-0.3px' }}>Finance</div>
              <div style={{ fontSize: '12.5px', color: MUTED, marginTop: '2px' }}>PageConnect P&amp;L — income, costs and profit</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {saved && <span style={{ fontSize: '12px', fontWeight: 600, color: GREEN }}>{saved}</span>}
              <button onClick={fetchAll}
                style={{ fontSize: '12px', fontWeight: 600, color: MUTED, background: WHITE, border: '1px solid ' + BORDER, padding: '7px 14px', borderRadius: '9px', cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
                ↻ Refresh
              </button>
            </div>
          </div>

          {/* Period selector */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '14px', flexWrap: 'wrap' }}>
            {PERIODS.map(p => (
              <button key={p.key} onClick={() => setPeriod(p.key)}
                style={{ padding: '6px 14px', borderRadius: '20px', border: '1px solid ' + (period === p.key ? BLUE : BORDER), background: period === p.key ? BLUE : WHITE, color: period === p.key ? WHITE : MUTED, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
                {p.label}
              </button>
            ))}
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '4px', padding: '4px', background: WHITE, border: '1px solid ' + BORDER, borderRadius: '10px', marginBottom: '18px', width: 'fit-content' }}>
            {TABS.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                style={{ padding: '7px 16px', borderRadius: '7px', border: 'none', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', background: activeTab === tab.key ? BLUE : 'transparent', color: activeTab === tab.key ? WHITE : MUTED, fontFamily: 'Space Grotesk, sans-serif', transition: 'all 0.12s', whiteSpace: 'nowrap' }}>
                {tab.label}
              </button>
            ))}
          </div>

          {loading && (
            <div style={{ textAlign: 'center', padding: '60px', color: MUTED, fontSize: '13px' }}>Loading finance data...</div>
          )}

          {!loading && activeTab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

              {/* Hero banner */}
              <div style={{ background: 'linear-gradient(135deg, #16A34A 0%, #0F766E 100%)', borderRadius: '14px', padding: '24px', color: WHITE }}>
                <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '4px' }}>Total income — {PERIODS.find(p => p.key === period)?.label}</div>
                <div style={{ fontSize: '32px', fontWeight: 800, letterSpacing: '-1px', marginBottom: '6px' }}>{fmt(stats.totalIncome)}</div>
                <div style={{ fontSize: '12px', opacity: 0.75 }}>
                  ≈ ${Math.round(stats.totalIncomeUSD).toLocaleString()} USD &nbsp;·&nbsp; {margin}% net margin
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '10px', marginTop: '16px' }}>
                  {[
                    { label: 'Net profit', value: fmt(stats.netProfit), color: stats.netProfit >= 0 ? '#BBF7D0' : '#FCA5A5' },
                    { label: 'After tax', value: fmt(profitAfterTax), color: '#BBF7D0' },
                    { label: 'Total costs', value: fmt(totalCosts), color: '#FCA5A5' },
                  ].map(s => (
                    <div key={s.label} style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '10px', padding: '12px' }}>
                      <div style={{ fontSize: '10px', opacity: 0.7, marginBottom: '4px' }}>{s.label}</div>
                      <div style={{ fontSize: '17px', fontWeight: 800, color: s.color }}>{s.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* KPI row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px' }} className="fin-kpi">
                <KpiCard label="Subscriptions" value={fmt(stats.subscriptionIncome)} sub="Main revenue stream" accent={BLUE}   iconPath={IC.income} />
                <KpiCard label="Ad slots"      value={fmt(stats.adIncome)}           sub="Advertising revenue"  accent={PURPLE} iconPath={IC.income} />
                <KpiCard label="Services"      value={fmt(stats.serviceIncome)}      sub="Setup & consulting"   accent={AMBER}  iconPath={IC.income} />
                <KpiCard label="Fixed costs"   value={fmt(stats.totalFixedCosts)}    sub="Monthly recurring"    accent={RED}    iconPath={IC.cost} />
              </div>

              {/* Charts */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '14px' }} className="fin-charts">
                <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER }}>
                  <div style={{ padding: '12px 18px', borderBottom: '1px solid ' + BORDER }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>Income trend</div>
                    <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>Last 6 months</div>
                  </div>
                  <div style={{ padding: '16px 18px' }}>
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={stats.monthlyTrend} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: MUTED }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: MUTED }} width={50} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? '৳' + Math.round(v / 1000) + 'k' : '৳' + v} />
                        <Tooltip contentStyle={{ border: '1px solid ' + BORDER, borderRadius: '10px', fontSize: '12px', fontFamily: 'Space Grotesk' }} formatter={v => ['৳' + v.toLocaleString(), 'Income']} />
                        <Bar dataKey="income" fill={GREEN} radius={[5, 5, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER }}>
                  <div style={{ padding: '12px 18px', borderBottom: '1px solid ' + BORDER }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>Income by stream</div>
                    <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>This period</div>
                  </div>
                  <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {Object.entries(stats.incomeByStream).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([stream, amount], i) => {
                      const pct = stats.totalIncome > 0 ? Math.round((amount / stats.totalIncome) * 100) : 0
                      const colors = [GREEN, BLUE, PURPLE, AMBER, RED, MUTED]
                      return (
                        <div key={stream}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ fontSize: '11.5px', color: TEXT }}>{stream}</span>
                            <span style={{ fontSize: '11.5px', fontWeight: 700, color: MUTED }}>{pct}%</span>
                          </div>
                          <div style={{ height: '5px', background: BG, borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: pct + '%', background: colors[i % colors.length], borderRadius: '3px', transition: 'width 0.3s' }} />
                          </div>
                        </div>
                      )
                    })}
                    {Object.keys(stats.incomeByStream).length === 0 && (
                      <div style={{ fontSize: '12px', color: MUTED, textAlign: 'center', padding: '20px 0' }}>No income this period</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Tax + USD info */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }} className="fin-kpi">
                <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px 18px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT, marginBottom: '4px' }}>Tax provision</div>
                  <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '12px' }}>Estimated {taxRate}% of net profit</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input type="number" value={taxRate} onChange={e => setTaxRate(Number(e.target.value))} min={0} max={50}
                      style={{ width: '70px', border: '1px solid ' + BORDER, borderRadius: '8px', padding: '7px 10px', fontSize: '13px', fontWeight: 700, textAlign: 'center', outline: 'none', fontFamily: 'Space Grotesk, sans-serif' }} />
                    <span style={{ fontSize: '12px', color: MUTED }}>% of net profit</span>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: AMBER, marginLeft: 'auto' }}>{fmt(taxAmount)} estimated</span>
                  </div>
                </div>
                <div style={{ background: '#EFF6FF', borderRadius: '12px', border: '1px solid #BFDBFE', padding: '16px 18px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: BLUE, marginBottom: '4px' }}>Yearly projection</div>
                  <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '10px' }}>Based on current period pace</div>
                  <div style={{ fontSize: '20px', fontWeight: 800, color: GREEN, marginBottom: '2px' }}>{fmt(stats.totalIncome * multiplier)}</div>
                  <div style={{ fontSize: '11.5px', color: MUTED }}>Projected annual income · {fmt(stats.netProfit * multiplier)} net</div>
                </div>
              </div>

            </div>
          )}

          {!loading && activeTab === 'income' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

              {canEdit && (
                <button onClick={() => setShowAddIncome(!showAddIncome)}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', padding: '11px', borderRadius: '10px', background: showAddIncome ? BG : GREEN, border: '1px solid ' + (showAddIncome ? BORDER : GREEN), fontSize: '13px', fontWeight: 700, color: showAddIncome ? MUTED : WHITE, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
                  <Icon path={IC.plus} color={showAddIncome ? MUTED : WHITE} size={14} sw={2.5} />
                  {showAddIncome ? 'Cancel' : 'Record income'}
                </button>
              )}

              {showAddIncome && canEdit && (
                <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '20px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: GREEN, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '14px' }}>Record income</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div style={{ gridColumn: 'span 2' }}>
                      <SField label="Income stream *" value={newIncome.stream} onChange={v => setNewIncome(i => ({ ...i, stream: v }))} options={INCOME_STREAMS} />
                    </div>
                    <SField label="Amount *" value={newIncome.amount} onChange={v => setNewIncome(i => ({ ...i, amount: v }))} type="number" placeholder="0" />
                    <SField label="Currency" value={newIncome.currency} onChange={v => setNewIncome(i => ({ ...i, currency: v }))} options={CURRENCIES.map(c => ({ key: c, label: c }))} />
                    <SField label="Payment method" value={newIncome.payment_method} onChange={v => setNewIncome(i => ({ ...i, payment_method: v }))} options={PAYMENT_METHODS.map(m => ({ key: m, label: m }))} />
                    <SField label="Date received" value={newIncome.received_at} onChange={v => setNewIncome(i => ({ ...i, received_at: v }))} type="date" />
                    <div style={{ gridColumn: 'span 2' }}>
                      <SField label="Seller" value={newIncome.seller_id} onChange={v => setNewIncome(i => ({ ...i, seller_id: v }))} optional
                        options={[{ key: '', label: 'No specific seller' }, ...sellers.map(s => ({ key: s.id, label: s.shop_name }))]} />
                    </div>
                    <SField label="Plan (if subscription)" value={newIncome.plan} onChange={v => setNewIncome(i => ({ ...i, plan: v }))} optional
                      options={[{ key: '', label: 'Not applicable' }, { key: 'starter', label: 'Starter' }, { key: 'growth', label: 'Growth' }, { key: 'pro', label: 'Pro' }, { key: 'business', label: 'Business' }]} />
                    <SField label="Period (months)" value={newIncome.period_months} onChange={v => setNewIncome(i => ({ ...i, period_months: v }))} type="number" />
                    <div style={{ gridColumn: 'span 2' }}>
                      <SField label="Notes" value={newIncome.notes} onChange={v => setNewIncome(i => ({ ...i, notes: v }))} placeholder="e.g. bKash ref #12345" optional />
                    </div>
                  </div>
                  <button onClick={handleAddIncome} disabled={savingIncome || !newIncome.amount || !newIncome.stream}
                    style={{ width: '100%', padding: '11px', borderRadius: '10px', background: savingIncome ? BORDER : GREEN, border: 'none', fontSize: '13px', fontWeight: 700, color: WHITE, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
                    {savingIncome ? 'Saving...' : 'Record income'}
                  </button>
                </div>
              )}

              {/* Income table */}
              <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, overflow: 'hidden' }}>
                <div style={{ padding: '12px 18px', borderBottom: '1px solid ' + BORDER, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>Income records</div>
                    <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>{income.length} entries this period</div>
                  </div>
                  <span style={{ fontSize: '13px', fontWeight: 800, color: GREEN }}>{fmt(stats.totalIncome)}</span>
                </div>
                {income.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: MUTED, fontSize: '12px' }}>No income recorded for this period</div>
                ) : (
                  <div>
                    {/* Table header */}
                    <div style={{ display: 'grid', gridTemplateColumns: '32px 1fr 100px 80px 110px 80px 36px', gap: '8px', padding: '7px 16px', background: BG, borderBottom: '1px solid ' + BORDER }}>
                      {['#', 'Stream / Seller', 'Method', 'Currency', 'Date', 'Amount', ''].map(h => (
                        <span key={h} style={{ fontSize: '10px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</span>
                      ))}
                    </div>
                    {incomePaged.map((item, i) => {
                      const stream  = INCOME_STREAMS.find(s => s.key === item.stream)
                      const isOpen  = expandedIncome === item.id
                      return (
                        <div key={item.id} style={{ borderBottom: '1px solid #F5F5F5' }}>
                          <div
                            onClick={() => setExpandedIncome(isOpen ? null : item.id)}
                            style={{ display: 'grid', gridTemplateColumns: '32px 1fr 100px 80px 110px 80px 36px', gap: '8px', padding: '10px 16px', alignItems: 'center', cursor: 'pointer' }}
                            onMouseEnter={e => e.currentTarget.style.background = BG}
                            onMouseLeave={e => e.currentTarget.style.background = WHITE}>
                            <span style={{ fontSize: '11px', color: MUTED, fontWeight: 600 }}>{(incomePage - 1) * PAGE_SIZE + i + 1}</span>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: '12.5px', fontWeight: 600, color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{stream?.label || item.stream}</div>
                              {item.sellers?.shop_name && <div style={{ fontSize: '11px', color: MUTED }}>{item.sellers.shop_name}</div>}
                            </div>
                            <span style={{ fontSize: '11.5px', color: MUTED }}>{item.payment_method}</span>
                            <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 7px', borderRadius: '6px', background: item.currency === 'USD' ? '#EFF6FF' : '#F0FDF4', color: item.currency === 'USD' ? BLUE : GREEN }}>{item.currency}</span>
                            <span style={{ fontSize: '11.5px', color: MUTED }}>{new Date(item.received_at).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: '2-digit' })}</span>
                            <span style={{ fontSize: '13px', fontWeight: 800, color: GREEN }}>{item.currency === 'USD' ? '$' : '৳'}{Number(item.amount).toLocaleString()}</span>
                            {canEdit ? (
                              <button onClick={e => { e.stopPropagation(); if (window.confirm('Delete this income record?')) { supabase.from('admin_income').delete().eq('id', item.id).then(fetchAll) } }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Icon path={IC.trash} color={RED} size={13} sw={1.8} />
                              </button>
                            ) : <span />}
                          </div>
                          {isOpen && (
                            <div style={{ padding: '10px 16px 12px 56px', background: '#FAFAFA', borderTop: '1px solid #F5F5F5' }}>
                              <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                                {item.plan && <div><div style={{ fontSize: '10px', color: MUTED, marginBottom: '2px' }}>PLAN</div><div style={{ fontSize: '12px', fontWeight: 600, color: TEXT, textTransform: 'capitalize' }}>{item.plan}</div></div>}
                                {item.period_months && <div><div style={{ fontSize: '10px', color: MUTED, marginBottom: '2px' }}>PERIOD</div><div style={{ fontSize: '12px', fontWeight: 600, color: TEXT }}>{item.period_months} month{item.period_months > 1 ? 's' : ''}</div></div>}
                                {item.notes && <div><div style={{ fontSize: '10px', color: MUTED, marginBottom: '2px' }}>NOTES</div><div style={{ fontSize: '12px', color: TEXT }}>{item.notes}</div></div>}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                    {/* Pagination */}
                    {incomeTotalP > 1 && (
                      <div style={{ padding: '10px 16px', borderTop: '1px solid ' + BORDER, background: BG, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '12px', color: MUTED }}>
                          <strong style={{ color: TEXT }}>{(incomePage - 1) * PAGE_SIZE + 1}–{Math.min(incomePage * PAGE_SIZE, income.length)}</strong> of <strong style={{ color: TEXT }}>{income.length}</strong>
                        </span>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button onClick={() => setIncomePage(p => Math.max(1, p - 1))} disabled={incomePage === 1}
                            style={{ width: '28px', height: '28px', borderRadius: '7px', border: '1px solid ' + BORDER, background: WHITE, cursor: incomePage === 1 ? 'not-allowed' : 'pointer', opacity: incomePage === 1 ? 0.4 : 1, fontSize: '14px' }}>‹</button>
                          <span style={{ padding: '0 8px', lineHeight: '28px', fontSize: '12px', fontWeight: 600, color: TEXT }}>{incomePage}/{incomeTotalP}</span>
                          <button onClick={() => setIncomePage(p => Math.min(incomeTotalP, p + 1))} disabled={incomePage === incomeTotalP}
                            style={{ width: '28px', height: '28px', borderRadius: '7px', border: '1px solid ' + BORDER, background: WHITE, cursor: incomePage === incomeTotalP ? 'not-allowed' : 'pointer', opacity: incomePage === incomeTotalP ? 0.4 : 1, fontSize: '14px' }}>›</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {!loading && activeTab === 'costs' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

              {/* Cost KPIs */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px' }} className="fin-kpi">
                <KpiCard label="Total costs"      value={fmt(totalCosts)}                sub="This period"      accent={RED}    iconPath={IC.cost} />
                <KpiCard label="Fixed costs"      value={fmt(stats.totalFixedCosts)}    sub="Recurring monthly" accent={RED}   iconPath={IC.cost} />
                <KpiCard label="Variable costs"   value={fmt(stats.totalVariableCosts)} sub="Depends on usage"  accent={AMBER} iconPath={IC.cost} />
                <KpiCard label="One-time costs"   value={fmt(stats.totalOneTimeCosts)}  sub="Capital expenses"  accent={PURPLE} iconPath={IC.cost} />
              </div>

              {canEdit && (
                <button onClick={() => setShowAddCost(!showAddCost)}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', padding: '11px', borderRadius: '10px', background: showAddCost ? BG : BLUE, border: '1px solid ' + (showAddCost ? BORDER : BLUE), fontSize: '13px', fontWeight: 700, color: showAddCost ? MUTED : WHITE, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
                  <Icon path={IC.plus} color={showAddCost ? MUTED : WHITE} size={14} sw={2.5} />
                  {showAddCost ? 'Cancel' : 'Add cost'}
                </button>
              )}

              {showAddCost && canEdit && (
                <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '20px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: BLUE, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '14px' }}>New cost</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div style={{ gridColumn: 'span 2' }}>
                      <SField label="Cost name *" value={newCost.name} onChange={v => setNewCost(c => ({ ...c, name: v }))} placeholder="e.g. Vercel Pro plan" />
                    </div>
                    <SField label="Category" value={newCost.category} onChange={v => { const cat = COST_CATEGORIES.find(c => c.key === v); setNewCost(c => ({ ...c, category: v, cost_type: cat?.type || 'fixed' })) }} options={COST_CATEGORIES} />
                    <SField label="Type" value={newCost.cost_type} onChange={v => setNewCost(c => ({ ...c, cost_type: v }))} options={[{ key: 'fixed', label: 'Fixed' }, { key: 'variable', label: 'Variable' }, { key: 'one_time', label: 'One-time' }]} />
                    <SField label="Amount *" value={newCost.amount} onChange={v => setNewCost(c => ({ ...c, amount: v }))} type="number" placeholder="0" />
                    <SField label="Currency" value={newCost.currency} onChange={v => setNewCost(c => ({ ...c, currency: v }))} options={CURRENCIES.map(c => ({ key: c, label: c }))} />
                    <SField label="Frequency" value={newCost.frequency} onChange={v => setNewCost(c => ({ ...c, frequency: v }))} options={[{ key: 'monthly', label: 'Monthly' }, { key: 'yearly', label: 'Yearly' }, { key: 'one_time', label: 'One-time' }]} />
                    <SField label="Employee role" value={newCost.employee_role} onChange={v => setNewCost(c => ({ ...c, employee_role: v }))} placeholder="e.g. Support staff" optional />
                    <div style={{ gridColumn: 'span 2' }}>
                      <SField label="Notes" value={newCost.notes} onChange={v => setNewCost(c => ({ ...c, notes: v }))} placeholder="Optional" optional />
                    </div>
                  </div>
                  <button onClick={handleAddCost} disabled={savingCost || !newCost.name || !newCost.amount}
                    style={{ width: '100%', padding: '11px', borderRadius: '10px', background: savingCost ? BORDER : BLUE, border: 'none', fontSize: '13px', fontWeight: 700, color: WHITE, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
                    {savingCost ? 'Saving...' : 'Add cost'}
                  </button>
                </div>
              )}

              {/* Costs table */}
              <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, overflow: 'hidden' }}>
                <div style={{ padding: '12px 18px', borderBottom: '1px solid ' + BORDER, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>All costs</div>
                    <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>{costs.length} cost entries</div>
                  </div>
                  <span style={{ fontSize: '13px', fontWeight: 800, color: RED }}>{fmt(totalCosts)}</span>
                </div>
                {costs.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: MUTED, fontSize: '12px' }}>No costs added yet</div>
                ) : (
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: '32px 1fr 90px 80px 90px 80px 36px', gap: '8px', padding: '7px 16px', background: BG, borderBottom: '1px solid ' + BORDER }}>
                      {['#', 'Name / Category', 'Type', 'Currency', 'Frequency', 'Amount', ''].map(h => (
                        <span key={h} style={{ fontSize: '10px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</span>
                      ))}
                    </div>
                    {costPaged.map((cost, i) => {
                      const isOpen = expandedCost === cost.id
                      const typeColors = { fixed: { bg: '#FEF2F2', color: RED }, variable: { bg: '#FFFBEB', color: AMBER }, one_time: { bg: '#F5F3FF', color: PURPLE } }
                      const tc = typeColors[cost.cost_type] || typeColors.fixed
                      return (
                        <div key={cost.id} style={{ borderBottom: '1px solid #F5F5F5' }}>
                          <div
                            onClick={() => setExpandedCost(isOpen ? null : cost.id)}
                            style={{ display: 'grid', gridTemplateColumns: '32px 1fr 90px 80px 90px 80px 36px', gap: '8px', padding: '10px 16px', alignItems: 'center', cursor: 'pointer' }}
                            onMouseEnter={e => e.currentTarget.style.background = BG}
                            onMouseLeave={e => e.currentTarget.style.background = WHITE}>
                            <span style={{ fontSize: '11px', color: MUTED, fontWeight: 600 }}>{(costPage - 1) * PAGE_SIZE + i + 1}</span>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: '12.5px', fontWeight: 600, color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cost.name}</div>
                              <div style={{ fontSize: '11px', color: MUTED, textTransform: 'capitalize' }}>{COST_CATEGORIES.find(c => c.key === cost.category)?.label || cost.category}</div>
                            </div>
                            <span style={{ fontSize: '10.5px', fontWeight: 700, padding: '2px 7px', borderRadius: '6px', background: tc.bg, color: tc.color, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{cost.cost_type.replace('_', '-')}</span>
                            <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 7px', borderRadius: '6px', background: cost.currency === 'USD' ? '#EFF6FF' : '#F0FDF4', color: cost.currency === 'USD' ? BLUE : GREEN }}>{cost.currency}</span>
                            <span style={{ fontSize: '11.5px', color: MUTED, textTransform: 'capitalize' }}>{cost.frequency}</span>
                            <span style={{ fontSize: '13px', fontWeight: 800, color: RED }}>{cost.currency === 'USD' ? '$' : '৳'}{Number(cost.amount).toLocaleString()}</span>
                            {canEdit ? (
                              <button onClick={e => { e.stopPropagation(); if (window.confirm('Delete this cost?')) { supabase.from('admin_costs').delete().eq('id', cost.id).then(fetchAll) } }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Icon path={IC.trash} color={RED} size={13} sw={1.8} />
                              </button>
                            ) : <span />}
                          </div>
                          {isOpen && cost.notes && (
                            <div style={{ padding: '10px 16px 12px 56px', background: '#FAFAFA', borderTop: '1px solid #F5F5F5' }}>
                              <div style={{ fontSize: '10px', color: MUTED, marginBottom: '2px' }}>NOTES</div>
                              <div style={{ fontSize: '12px', color: TEXT }}>{cost.notes}</div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                    {costTotalP > 1 && (
                      <div style={{ padding: '10px 16px', borderTop: '1px solid ' + BORDER, background: BG, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '12px', color: MUTED }}>
                          <strong style={{ color: TEXT }}>{(costPage - 1) * PAGE_SIZE + 1}–{Math.min(costPage * PAGE_SIZE, costs.length)}</strong> of <strong style={{ color: TEXT }}>{costs.length}</strong>
                        </span>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button onClick={() => setCostPage(p => Math.max(1, p - 1))} disabled={costPage === 1}
                            style={{ width: '28px', height: '28px', borderRadius: '7px', border: '1px solid ' + BORDER, background: WHITE, cursor: costPage === 1 ? 'not-allowed' : 'pointer', opacity: costPage === 1 ? 0.4 : 1, fontSize: '14px' }}>‹</button>
                          <span style={{ padding: '0 8px', lineHeight: '28px', fontSize: '12px', fontWeight: 600, color: TEXT }}>{costPage}/{costTotalP}</span>
                          <button onClick={() => setCostPage(p => Math.min(costTotalP, p + 1))} disabled={costPage === costTotalP}
                            style={{ width: '28px', height: '28px', borderRadius: '7px', border: '1px solid ' + BORDER, background: WHITE, cursor: costPage === costTotalP ? 'not-allowed' : 'pointer', opacity: costPage === costTotalP ? 0.4 : 1, fontSize: '14px' }}>›</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {!loading && activeTab === 'pnl' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

              {/* P&L Statement */}
              <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, overflow: 'hidden' }}>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid ' + BORDER, background: BG }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>Profit &amp; Loss Statement</div>
                  <div style={{ fontSize: '11.5px', color: MUTED, marginTop: '2px' }}>PageConnect · {PERIODS.find(p => p.key === period)?.label} · Confidential</div>
                </div>
                <div style={{ padding: '16px 20px' }}>
                  {[
                    { label: 'Total income',          value: stats.totalIncome,          bold: true, color: TEXT },
                    { label: '  Subscription income', value: stats.subscriptionIncome,   indent: true },
                    { label: '  Ad slot income',      value: stats.adIncome,             indent: true },
                    { label: '  Service income',      value: stats.serviceIncome,        indent: true },
                    { label: '  Other income',        value: stats.otherIncome,          indent: true },
                    { label: '— Fixed costs',         value: -stats.totalFixedCosts,     color: RED },
                    { label: '— Variable costs',      value: -stats.totalVariableCosts,  color: AMBER },
                    { label: '— One-time costs',      value: -stats.totalOneTimeCosts,   color: PURPLE },
                    { label: '= Operating profit',    value: stats.netProfit,            bold: true, border: true, color: stats.netProfit >= 0 ? GREEN : RED },
                    { label: 'Tax provision (' + taxRate + '%)', value: -taxAmount,      color: MUTED },
                    { label: '= Net profit after tax', value: profitAfterTax,            bold: true, border: true, color: profitAfterTax >= 0 ? BLUE : RED },
                  ].map((row, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: i < 10 ? '1px solid #F5F5F5' : 'none', borderTop: row.border ? '2px solid ' + BORDER : 'none', marginTop: row.border ? '4px' : '0' }}>
                      <span style={{ fontSize: '12.5px', color: row.indent ? MUTED : TEXT, fontWeight: row.bold ? 700 : 400, paddingLeft: row.indent ? '16px' : '0' }}>{row.label}</span>
                      <span style={{ fontSize: '13px', fontWeight: row.bold ? 800 : 600, color: row.color || MUTED }}>
                        {row.value < 0 ? '— ' + fmt(Math.abs(row.value)) : fmt(row.value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Yearly projection */}
              <div style={{ background: '#EFF6FF', borderRadius: '12px', border: '1px solid #BFDBFE', padding: '18px 20px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: BLUE, marginBottom: '12px' }}>Yearly projection</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px' }} className="fin-kpi">
                  {[
                    { label: 'Annual income',  value: fmt(stats.totalIncome * multiplier) },
                    { label: 'Annual costs',   value: fmt(totalCosts * multiplier) },
                    { label: 'Annual profit',  value: fmt(stats.netProfit * multiplier) },
                    { label: 'Net margin',     value: margin + '%' },
                  ].map(item => (
                    <div key={item.label} style={{ background: WHITE, borderRadius: '10px', padding: '12px 14px' }}>
                      <div style={{ fontSize: '11px', color: MUTED, marginBottom: '4px' }}>{item.label}</div>
                      <div style={{ fontSize: '16px', fontWeight: 800, color: BLUE }}>{item.value}</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: '11.5px', color: '#3B82F6', marginTop: '10px' }}>Based on current period extrapolated. Actual results may vary.</div>
              </div>

              {/* USD view */}
              <div style={{ background: '#F5F3FF', borderRadius: '12px', border: '1px solid #DDD6FE', padding: '18px 20px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: PURPLE, marginBottom: '12px' }}>International view (USD) — at ৳{usdRate}/USD</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                  {[
                    { label: 'Total income', value: '$' + Math.round(stats.totalIncome / usdRate).toLocaleString() },
                    { label: 'Total costs',  value: '$' + Math.round(totalCosts / usdRate).toLocaleString() },
                    { label: 'Net profit',   value: '$' + Math.round(stats.netProfit / usdRate).toLocaleString() },
                  ].map(item => (
                    <div key={item.label} style={{ background: WHITE, borderRadius: '10px', padding: '12px 14px' }}>
                      <div style={{ fontSize: '11px', color: MUTED, marginBottom: '4px' }}>{item.label}</div>
                      <div style={{ fontSize: '16px', fontWeight: 800, color: PURPLE }}>{item.value}</div>
                    </div>
                  ))}
                </div>

                <div style={{ fontSize: '11.5px', color: '#8B5CF6', marginTop: '10px' }}>Based on current USD rate. Actual rates may vary.</div>
              </div>

              <div style={{ background: BG, borderRadius: '10px', border: '1px solid ' + BORDER, padding: '14px 16px', fontSize: '12px', color: MUTED, lineHeight: 1.7 }}>
                This P&amp;L is generated from income and costs recorded manually in this panel. Tax provision is an estimate only. Always consult a qualified accountant for accurate financial reporting and tax filing.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AdminFinance